import React, { forwardRef, useCallback } from 'react';
import classnames from 'classnames';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import IconButton, { IconButtonTheme, IconButtonSize } from '../IconButton';
import Icon, { IconTheme, IconSize } from '../Icon';

import { SearchInputProps, Theme, Variant } from './SearchInput.types';
import SearchInputThemer from './SearchInput.theme';

const ClearIconButtonThemeMap = {
  [Theme.default]: IconButtonTheme.dark,
  [Theme.primary]: IconButtonTheme.dark,
  [Theme.secondary]: IconButtonTheme.dark,
  [Theme.inverse]: IconButtonTheme.light,
};
const ClearIconThemeMap = {
  [Theme.default]: IconTheme.dark,
  [Theme.primary]: IconTheme.dark,
  [Theme.secondary]: IconTheme.dark,
  [Theme.inverse]: IconTheme.light,
};

const SearchInputComponent = forwardRef<HTMLDivElement, SearchInputProps>((props, ref) => {
  const {
    id, className, style, theme,
    disabled,
    callbackValue,
    placeHolder,
    value, onChange,
    onFocus, onBlur, onClear,
    showClearButton, showClearIconButton, startAdornmentNode,
    autoComplete, onKeyPress,
  } = props;
  // const classes = SearchInputThemer.useStyles;
  const defaultInputClasses = SearchInputThemer.useDefaultTextFieldStyles;
  const primaryInputClasses = SearchInputThemer.usePrimaryTextFieldStyles;
  const secondaryInputClasses = SearchInputThemer.useSecondaryTextFieldStyles;
  const inverseInputClasses = SearchInputThemer.useInverseTextFieldStyles;
  const themeClassesMap = {
    [Theme.default]: defaultInputClasses,
    [Theme.primary]: primaryInputClasses,
    [Theme.secondary]: secondaryInputClasses,
    [Theme.inverse]: inverseInputClasses,
  };

  const handleChange = useCallback((event: any) => {
    onChange(event, callbackValue);
  }, [onChange, callbackValue]);
  const handleFocus = useCallback((event: any) => {
    onFocus?.(event, callbackValue);
  }, [onFocus, callbackValue]);
  const handleBlur = useCallback((event: any) => {
    onBlur?.(event, callbackValue);
  }, [onBlur, callbackValue]);

  return (
    <OutlinedInput
      id={id}
      ref={ref}
      notched
      disabled={!!disabled}
      classes={themeClassesMap[theme || Theme.primary] as any}
      sx={{width: '100%',
        height: 36,
        color: '#FFFFFF',
        border: '1px solid #FFFFFF',
        padding: '8px 12px',
        fontSize: 14,
        borderRadius: '18px',
        fontWeight: 400,
      }}
      // className={classnames(className, classes.searchInput)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={style}
      label={undefined}
      placeholder={placeHolder}
      autoComplete={autoComplete}
      onKeyPress={onKeyPress}
      startAdornment={(
        <InputAdornment position="start">
          {startAdornmentNode}
        </InputAdornment>
      )}
      endAdornment={showClearButton && (
        <InputAdornment position="end">
          {showClearIconButton && (
            <IconButton
              id={`${id}-ClearButton`}
              theme={ClearIconButtonThemeMap[theme || Theme.primary]}
              size={IconButtonSize.small}
              onClick={onClear}
              callbackValue={callbackValue}
              disabled={disabled}
            >
              <Icon style={{color: '#4C4C4C'}} size={IconSize.massive}>
                cancel
              </Icon>
            </IconButton>
          )}
          {!showClearIconButton && (
            <IconButton
              id={`${id}-ClearButton`}
              theme={ClearIconButtonThemeMap[theme || Theme.primary]}
              size={IconButtonSize.small}
              onClick={onClear}
              callbackValue={callbackValue}
              disabled={disabled}
            >
              <Icon theme={ClearIconThemeMap[theme || Theme.primary]} size={IconSize.small}>
                clear
              </Icon>
            </IconButton>
          )}
        </InputAdornment>
      )}
      value={value}
      onChange={handleChange}
    />
  );
});

SearchInputComponent.defaultProps = {
  className: undefined,
  style: undefined,
  theme: Theme.primary,
  variant: Variant.rounded,
  disabled: false,
  callbackValue: undefined,
  placeHolder: 'Type to Search',
  showClearButton: false,
  showClearIconButton: false,
  autoComplete: 'off',
  startAdornmentNode: (<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} size='lg' style={{ color: "#FFFFFF"}} />),
  onFocus: () => null,
  onBlur: () => null,
  onClear: () => null,
  onKeyPress: () => null,
};

export { Theme as SearchInputTheme, Variant as SearchInputVariant };
export default SearchInputComponent;
